import { sortCollectionAlphabetically } from "@jugl-web/utils/array/sortCollectionAlphabetically";
import { useMemo } from "react";
import { useBoardPermissionsFactory } from "./useBoardPermissions";
import { useTaskBoardsQuery } from "./useTaskBoardsQuery";

interface UseTaskBoardListOptions {
  entityId: string;
  meId: string;
}

export const useTaskBoardList = ({
  entityId,
  meId,
}: UseTaskBoardListOptions) => {
  const { cachedBoards } = useTaskBoardsQuery({ entityId });

  const getBoardPermissions = useBoardPermissionsFactory({ entityId, meId });

  const sortedBoards = useMemo(
    () => sortCollectionAlphabetically(cachedBoards, (board) => board.name),
    [cachedBoards]
  );

  /**
   * Boards that are accessible by the current user, excluding boards
   * that were requested on-demand (by non-board members).
   * It's mostly used to render board lists in the UI.
   */
  const accessibleBoards = useMemo(
    () =>
      sortedBoards.filter(
        (board) => getBoardPermissions(board.id).canAccessBoard
      ),
    [sortedBoards, getBoardPermissions]
  );

  const boardsWithTaskCreationPermission = useMemo(
    () =>
      accessibleBoards.filter(
        (board) => getBoardPermissions(board.id).canCreateTasks
      ),
    [accessibleBoards, getBoardPermissions]
  );

  return {
    accessibleBoards,
    boardsWithTaskCreationPermission,
  };
};
